// src/pages/user/UserDetailPage.tsx
// Full user profile view with all fields.

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useUser, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, ErrorAlert} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatUserStatus } from '../../features/user/logic';
import type { User } from '@core/types/user';

export const UserDetailPage = observer(() => {
  const userStore = useUser();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) userStore.GetUser(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const user = userStore.currentItem as User | null;
  if (userStore.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!user) return <ErrorAlert error={t('common.notFound')} />;

  const status = formatUserStatus(user.status ?? '');

  const handleDelete = async () => {
    if (!confirm(t('user.deleteConfirm'))) return;
    await userStore.DeleteUser(user.entity?.id ?? '');
    navigate('/users');
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/users" className="text-sm text-primary hover:text-primary/80">{t('common.backToList')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{user.email}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/users/${user.entity?.id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {userStore.error ? <ErrorAlert error={userStore.error} dismissible onDismiss={() => userStore.clearError()} /> : null}

      <Card>
        <CardHeader><CardTitle>{t('user.details')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={user.entity?.id} mono />
            <Field label={t('auth.email')} value={user.email} />
            <Field label={t('auth.username')} value={user.username} />
            <Field label={t('user.phone')} value={user.phone} />
            <Field label={t('common.status')} value={<StatusBadge variant={status.variant} label={t(status.labelKey)} />} />
            <Field label={t('user.emailVerified')} value={<Badge variant={user.email_verified ? 'success' : 'secondary'}>{user.email_verified ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('user.phoneVerified')} value={<Badge variant={user.phone_verified ? 'success' : 'secondary'}>{user.phone_verified ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('user.twoFactor')} value={<Badge variant={user.two_factor_enabled ? 'success' : 'secondary'}>{user.two_factor_enabled ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('user.inviteCode')} value={user.invite_code} mono />
            <Field label={t('common.createdAt')} value={user.created_at} />
            <Field label={t('common.updatedAt')} value={user.updated_at} />
            <Field label={t('user.lastSeen')} value={user.last_seen_at} />
            {user.deleted_at && <Field label={t('user.deletedAt')} value={user.deleted_at} />}
          </dl>
        </CardContent>
      </Card>

      {user.resources && (
        <Card>
          <CardHeader><CardTitle>{t('user.resources')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(user.resources, null, 2)}</pre></CardContent>
        </Card>
      )}

      {user.aspects?.has_aspects && (
        <Card>
          <CardHeader><CardTitle>{t('user.aspects')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(user.aspects, null, 2)}</pre></CardContent>
        </Card>
      )}
    </div>
  );
});

export default UserDetailPage;
